import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRoles } from '@/lib/api/hooks';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Plus, Shield, Trash2, Edit } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PermissionGuard } from '@/components/guards/PermissionGuard';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

export default function RolesPage() {
    const navigate = useNavigate();
    const { roles, isLoading, create, delete: deleteRole } = useRoles();
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [newRole, setNewRole] = useState({ name: '', description: '' });
    const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string }>({
        open: false,
        id: '',
        name: '',
    });

    const handleCreate = async () => {
        if (!newRole.name) return;

        try {
            await create(newRole);
            setIsCreateDialogOpen(false);
            setNewRole({ name: '', description: '' });
        } catch (error) {
        }
    };

    const handleDeleteClick = (id: string, name: string) => {
        setDeleteConfirm({ open: true, id, name });
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteRole(deleteConfirm.id);
            setDeleteConfirm({ open: false, id: '', name: '' });
        } catch (error) {
        }
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Roles & Permissions"
                action={
                    <PermissionGuard resource="roles" action="create">
                        <Button onClick={() => setIsCreateDialogOpen(true)}>
                            <Plus className="h-4 w-4 mr-2" />
                            Create Role
                        </Button>
                    </PermissionGuard>
                }
            />

            {isLoading ? (
                <div className="text-center py-12">Loading roles...</div>
            ) : !roles || roles.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center">
                        <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">No roles found</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {roles.map((role) => (
                        <Card key={role.id}>
                            <CardHeader>
                                <div className="flex items-start justify-between">
                                    <div className="space-y-1">
                                        <CardTitle className="flex items-center gap-2">
                                            {role.name}
                                            {role.isSystem && (
                                                <Badge variant="secondary" className="text-xs">
                                                    System
                                                </Badge>
                                            )}
                                        </CardTitle>
                                        {role.description && (
                                            <CardDescription>{role.description}</CardDescription>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <PermissionGuard resource="roles" action="update">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => navigate(`/roles/${role.id}`)}
                                            >
                                                <Edit className="h-4 w-4" />
                                            </Button>
                                        </PermissionGuard>
                                        {!role.isSystem && (
                                            <PermissionGuard resource="roles" action="delete">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleDeleteClick(role.id, role.name)}
                                                >
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            </PermissionGuard>
                                        )}
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="text-sm text-muted-foreground">
                                    Created {new Date(role.createdAt).toLocaleDateString()}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Create Role Dialog */}
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create New Role</DialogTitle>
                        <DialogDescription>
                            Create a new role to assign to users. You can configure permissions later.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">Role Name</Label>
                            <Input
                                id="name"
                                placeholder="e.g., Content Manager"
                                value={newRole.name}
                                onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="description">Description</Label>
                            <Textarea
                                id="description"
                                placeholder="Describe what this role can do..."
                                value={newRole.description}
                                onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                                rows={3}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreate} disabled={!newRole.name}>
                            Create Role
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                open={deleteConfirm.open}
                onOpenChange={(open) => setDeleteConfirm({ ...deleteConfirm, open })}
                onConfirm={handleDeleteConfirm}
                title="Delete Role"
                description={`Are you sure you want to delete the role "${deleteConfirm.name}"? This action cannot be undone and will remove all permissions associated with this role.`}
                confirmText="Delete Role"
                cancelText="Cancel"
                variant="destructive"
            />
        </div>
    );
}
